import { Button } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { useUserNotificationPreference } from "../../../hooks/api/v2/userNotificationPreference/useUserNotificationPreference";
import { useNavigate } from "react-router";
import { useSellarUser } from "../../../hooks/useSellarUser.shared";
import { ACCOUNT_NOTIFICATION_SETTINGS } from "../../../constants/routes";
import { PreferenceCheckbox, Row, Title } from "./NotificationSettings";
import { SpinnerLegacy } from "../../../components";
import { getUserAccountTypes } from "../../../utils/getUserAccountTypes";

export const NotificationSettingsPromptDialog = ({
  onDismiss,
  active,
}: {
  onDismiss: () => void;
  active: boolean;
}) => {
  const navigate = useNavigate();
  const { user } = useSellarUser();

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title="Review your order update notification settings"
        description={`You have control over which emails and push notifications you want to receive. Emails are sent to ${user?.email}`}
      />
      {active && <NotificationPreferencesContent />}
      <DialogCTA>
        <Button
          className="w-full"
          onClick={() => navigate(ACCOUNT_NOTIFICATION_SETTINGS)}
        >
          Edit my notification preferences
        </Button>
      </DialogCTA>
    </Dialog>
  );
};

const NotificationPreferencesContent = () => {
  const { user } = useSellarUser();

  if (!user) throw new Error("Cannot find user");

  const {
    retailer: hasBuyerAccount,
    supplier: hasSupplierAccount,
    dual: hasDualAccount,
  } = getUserAccountTypes(user);

  const notificationPreferenceQuery = useUserNotificationPreference(
    user.UserNotificationPreferenceId,
  );

  const showPreferences =
    notificationPreferenceQuery.isSuccess && notificationPreferenceQuery.data;

  return (
    <DialogContent>
      {!showPreferences ? (
        <SpinnerLegacy />
      ) : (
        <div className="pointer-events-none mb-4 text-sm">
          <div className={"py-2"}>
            <Row
              text="Order Events"
              textClass="font-bold"
              rowClass="border-t bg-zinc-50 py-0"
            >
              <Title text="Email" />
              <Title text="Push" />
            </Row>

            <Row text="Order requested" rowClass="py-1">
              <PreferenceCheckbox
                checked={notificationPreferenceQuery.data.emailOrderRequested}
              />
              <PreferenceCheckbox
                checked={notificationPreferenceQuery.data.pushOrderRequested}
              />
            </Row>

            {hasBuyerAccount && (
              <Row text={`Order accepted${hasDualAccount ? "*" : ""}`}>
                <PreferenceCheckbox
                  checked={notificationPreferenceQuery.data.emailOrderAccepted}
                />
                <PreferenceCheckbox
                  checked={notificationPreferenceQuery.data.pushOrderAccepted}
                />
              </Row>
            )}

            {hasBuyerAccount && (
              <Row
                text={`Order dispatched${hasDualAccount ? "*" : ""}`}
                rowClass="py-1"
              >
                <PreferenceCheckbox
                  checked={
                    notificationPreferenceQuery.data.emailOrderDispatched
                  }
                />
                <PreferenceCheckbox
                  checked={notificationPreferenceQuery.data.pushOrderDispatched}
                />
              </Row>
            )}

            {hasBuyerAccount && (
              <Row
                text={`Order delivered${hasDualAccount ? "*" : ""}`}
                rowClass="py-1"
              >
                <PreferenceCheckbox
                  checked={notificationPreferenceQuery.data.emailOrderDelivered}
                />
                <PreferenceCheckbox
                  checked={notificationPreferenceQuery.data.pushOrderDelivered}
                />
              </Row>
            )}

            <Row text="Order cancelled" rowClass="py-1">
              <PreferenceCheckbox
                checked={notificationPreferenceQuery.data.emailOrderCancelled}
              />
              <PreferenceCheckbox
                checked={notificationPreferenceQuery.data.pushOrderCancelled}
              />
            </Row>

            <Row text="Comment added to order" rowClass="py-1">
              <PreferenceCheckbox
                checked={
                  notificationPreferenceQuery.data.emailOrderCommentCreated
                }
              />
              <PreferenceCheckbox
                checked={
                  notificationPreferenceQuery.data.pushOrderCommentCreated
                }
              />
            </Row>

            {hasSupplierAccount && (
              <Row
                text={`Order paid for by card payment link${hasDualAccount ? "**" : ""}`}
                rowClass="py-1"
              >
                <PreferenceCheckbox
                  checked={notificationPreferenceQuery.data.emailOrderPaid}
                />
                <PreferenceCheckbox
                  checked={notificationPreferenceQuery.data.pushOrderPaid}
                />
              </Row>
            )}
          </div>
        </div>
      )}
    </DialogContent>
  );
};
